import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Award, Users } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import CtaBand from "@/components/home/CtaBand";
import { COMPANY, IMAGES } from "@/data/content";

const CREDENTIALS = [
  { icon: ShieldCheck, label: "Authorised Credit Representative", value: `CRN ${COMPANY.crn} — ${COMPANY.aggregator} (ACL ${COMPANY.acl})` },
  { icon: Award, label: "External Dispute Resolution", value: `AFCA membership ${COMPANY.afcaMember}` },
  { icon: Users, label: "Network", value: "Part of Australia's largest broker community — 6,000+ brokers, 100+ lenders" },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="pt-36 sm:pt-44 pb-20 sm:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
            <div>
              <Reveal>
                <Eyebrow>About the principal</Eyebrow>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-ink leading-[1.05]">
                  Anu <span className="font-editorial italic font-medium text-gold-600">Prakash</span>
                </h1>
                <p className="mt-6 text-sm uppercase tracking-[0.25em] text-ink/45 font-semibold">
                  Principal · Invesmo Pty Ltd
                </p>
              </Reveal>

              <Reveal delay={0.1}>
                <div className="mt-10 space-y-6 text-ink/60 leading-relaxed">
                  <p>
                    Anu founded Invesmo on a simple conviction: that debt, properly structured, is a strategic asset —
                    and that most borrowers are never shown how.
                  </p>
                  <p>
                    His career spans senior positions across banking, institutional credit and property finance,
                    covering everything from frontline residential lending to complex, multi-party capital structuring
                    for businesses and developments. He has guided first-home buyers, medical and legal professionals,
                    family businesses and seasoned investors through every kind of market cycle.
                  </p>
                  <p>
                    Beyond finance, Anu brings decades of hands-on experience running businesses, developing property
                    and pursuing international trading opportunities. He hasn't just structured deals — he has built,
                    traded and developed, through booms and downturns alike.
                  </p>
                  <p>
                    That breadth is the point. When Anu assesses your scenario, it is with the judgment of someone who
                    has sat on both sides of the credit table — and run businesses of his own. He knows how credit
                    teams think, what policy exceptions exist, and which lender is genuinely right for the next decade
                    of your plans, not just the next settlement. Every lesson from that commercial life is now applied
                    to his advisory work at Invesmo.
                  </p>
                  <p>
                    Based in Camberwell, Melbourne, Anu advises clients across Australia in person, by phone and by
                    video — and stays engaged long after settlement through annual reviews and proactive rate and
                    structure management.
                  </p>
                </div>
              </Reveal>

              <Reveal delay={0.15}>
                <p className="mt-10 font-editorial italic text-xl sm:text-2xl text-ink/75 leading-relaxed border-l-2 border-gold pl-6">
                  "Good broking isn't finding a loan. It's structuring capital so the next decision is easier than the last."
                </p>
                <Link
                  to="/book"
                  data-testid="about-book-cta"
                  className="group mt-10 inline-flex items-center gap-3 rounded-full bg-obsidian text-gold px-8 py-4 text-sm font-semibold transition-all duration-300 hover:bg-gold hover:text-obsidian hover:shadow-gold-glow"
                >
                  Book a meeting with Anu
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Reveal>
            </div>

            <div className="lg:sticky lg:top-28 space-y-8">
              <Reveal delay={0.1}>
                <div className="relative">
                  <div className="absolute -inset-4 rounded-[2.5rem] bg-gradient-to-br from-gold/25 via-transparent to-brand/20 blur-xl pointer-events-none" />
                  <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] shadow-lift ring-1 ring-ink/5">
                    <img
                      src={IMAGES.principal}
                      alt="Anu Prakash, Principal of Invesmo Pty Ltd"
                      className="w-full h-full object-cover"
                      data-testid="about-portrait"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-obsidian/45 via-transparent to-transparent" />
                    <div className="absolute bottom-6 left-6 text-white">
                      <div className="font-display text-xl font-bold">Camberwell, Melbourne</div>
                      <div className="text-xs tracking-[0.2em] uppercase text-gold mt-1">Advising Australia-wide</div>
                    </div>
                  </div>
                </div>
              </Reveal>

              <Reveal delay={0.2}>
                <div className="rounded-3xl bg-white border border-ink/8 p-8 space-y-6" data-testid="about-credentials">
                  {CREDENTIALS.map((c) => (
                    <div key={c.label} className="flex items-start gap-4">
                      <span className="shrink-0 w-11 h-11 rounded-2xl bg-brand-light flex items-center justify-center">
                        <c.icon size={19} className="text-brand" strokeWidth={1.8} />
                      </span>
                      <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-ink/45 font-semibold">{c.label}</div>
                        <div className="text-sm text-ink mt-1 leading-relaxed">{c.value}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
